import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import {
  Badge,
  Checkbox,
  Icons,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { Pressable } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';

const PACK_DETAIL_SEPARATOR = ' · ';
const ADDED_OPACITY = 0.5;
// Aligns sub-row content under the parent row's text (past checkbox + thumbnail).
const SUB_ROW_PADDING = 56;
const CHEVRON_DOWN_ICON_SIZE = 14;

interface CatalogItemRowProps {
  addedPackUUIDs: Set<string>;
  expandable: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleItem: () => void;
  onTogglePack: (pack: OrderGuideItem) => void;
  packs: OrderGuideItem[];
  selectedPackUUIDs: Set<string>;
}

const packDetails = (pack: OrderGuideItem) =>
  [pack.packDisplayString, pack.externalID]
    .filter(Boolean)
    .join(PACK_DETAIL_SEPARATOR);

// Added packs are forced on and aren't selectable, so a group's coverage is
// added + selected. Full coverage → checked, partial → indeterminate.
const getParentChecked = (
  coveredCount: number,
  total: number,
): boolean | 'indeterminate' => {
  if (coveredCount === 0) {
    return false;
  }
  if (coveredCount === total) {
    return true;
  }
  return 'indeterminate';
};

export const CatalogItemRow: React.FC<CatalogItemRowProps> = ({
  addedPackUUIDs,
  expandable,
  expanded,
  onToggleExpand,
  onToggleItem,
  onTogglePack,
  packs,
  selectedPackUUIDs,
}) => {
  const [styles, theme] = useStyles();

  const rotationProgress = useDerivedValue(() =>
    withTiming(expanded ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  );
  const chevronStyle = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(rotationProgress.value, [0, 1], [0, 180])}deg` },
    ],
  }));

  const { displayName, imageURL } = packs[0];
  const addedCount = packs.filter((pack) =>
    addedPackUUIDs.has(pack.variantPackUUID),
  ).length;
  const selectedCount = packs.filter((pack) =>
    selectedPackUUIDs.has(pack.variantPackUUID),
  ).length;

  const addedLabel = (
    <Typography
      color={theme.sk.color.text.tertiary}
      flexShrink={0}
      marginRight={theme.sk.spacing.spaceXlarge}
      size="$xs"
    >
      {i18n.t('sales_portal.order_guides_list.add_items.added_label')}
    </Typography>
  );

  const thumbnail = (
    <ItemImage
      alt={displayName}
      cachePolicy="memory-disk"
      imageURLs={imageURL ? [imageURL] : []}
      size={Spacing.SPACE_2XLARGE}
    />
  );

  if (!expandable) {
    const pack = packs[0];
    const added = addedPackUUIDs.has(pack.variantPackUUID);
    const selected = selectedPackUUIDs.has(pack.variantPackUUID);
    const checked = added || selected;

    return (
      <Pressable
        accessibilityLabel={displayName}
        accessibilityRole="checkbox"
        accessibilityState={{ checked, disabled: added }}
        disabled={added}
        onPress={() => onTogglePack(pack)}
        style={({ hovered }) => [
          styles.row,
          styles.rowBorder,
          (selected || (hovered && !added)) && styles.selectedRow,
          added && styles.dimmed,
        ]}
      >
        <XStack flex={1} gap={theme.sk.spacing.spaceSMedium} minWidth={0}>
          <Checkbox
            checked={checked}
            disabled={added}
            pointerEvents="none"
            size="$sm"
          />
          {thumbnail}
          <YStack gap="$2" style={styles.identity}>
            <Typography
              color={theme.sk.color.text.primary}
              fontWeight="$medium"
              numberOfLines={1}
              size="$sm"
            >
              {displayName}
            </Typography>
            {packDetails(pack) ? (
              <Typography
                color={theme.sk.color.text.secondary}
                numberOfLines={1}
                size="$xs"
              >
                {packDetails(pack)}
              </Typography>
            ) : null}
          </YStack>
        </XStack>
        {added ? addedLabel : null}
      </Pressable>
    );
  }

  const allAdded = addedCount === packs.length;

  // Some — but not all — packs are already in the guide. Surfaced on the
  // collapsed parent the same way single packs show the "Added" label.
  const partlyAdded = addedCount > 0 && !allAdded;

  const parentChecked = getParentChecked(
    addedCount + selectedCount,
    packs.length,
  );

  // Shared by the row body and the chevron so both controls announce the same
  // expand/collapse purpose to screen readers.
  const expandLabel = i18n.t(
    expanded
      ? 'sales_portal.order_guides_list.add_items.collapse_packs'
      : 'sales_portal.order_guides_list.add_items.expand_packs',
  );

  const parentSubtitle = allAdded
    ? i18n.t('sales_portal.order_guides_list.add_items.added_label')
    : selectedCount > 0
      ? i18n.t('sales_portal.order_guides_list.add_items.packs_selected', {
          selected: selectedCount,
          total: packs.length,
        })
      : i18n.t('sales_portal.order_guides_list.add_items.pick_pack_sizes');

  const parentHoverStyle = {
    backgroundColor:
      !allAdded || expanded ? theme.sk.color.bg.primary.hover : 'transparent',
  };

  // accessibilityLabel collapses the subtree, so spell out the row's visible
  // content (name, pack count, added count, subtitle) plus the expand purpose.
  const bodyLabel = [
    displayName,
    i18n.t('sales_portal.order_guides_list.add_items.pack_count_plural', {
      count: packs.length,
    }),
    partlyAdded
      ? i18n.t('sales_portal.order_guides_list.add_items.pack_count_added', {
          added: addedCount,
        })
      : null,
    parentSubtitle,
    expandLabel,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <YStack
      borderBottomWidth={1}
      borderColor={theme.sk.color.border.primary}
      opacity={allAdded ? ADDED_OPACITY : 1}
    >
      <XStack
        backgroundColor={
          selectedCount > 0 || expanded
            ? theme.sk.color.bg.primary.hover
            : 'transparent'
        }
        hoverStyle={parentHoverStyle}
        paddingHorizontal={theme.sk.spacing.spaceSmall}
        paddingVertical={theme.sk.spacing.spaceSmall}
      >
        <XStack
          alignItems="center"
          flex={1}
          gap={theme.sk.spacing.spaceSMedium}
          minWidth={0}
        >
          {/* Checkbox selects every pack of the item. */}
          <Pressable
            accessibilityLabel={displayName}
            accessibilityRole="checkbox"
            accessibilityState={{
              checked:
                parentChecked === 'indeterminate' ? 'mixed' : parentChecked,
              disabled: allAdded,
            }}
            disabled={allAdded}
            onPress={onToggleItem}
          >
            <Checkbox
              checked={parentChecked}
              disabled={allAdded}
              pointerEvents="none"
              size="$sm"
            />
          </Pressable>
          {/* Everything but the checkbox expands/collapses the pack list. */}
          <Pressable
            accessibilityLabel={bodyLabel}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            onPress={onToggleExpand}
            style={styles.body}
          >
            {thumbnail}
            <YStack flex={1} gap="$2" style={styles.identity}>
              <XStack
                alignItems="center"
                gap={theme.sk.spacing.spaceSMedium}
                minWidth={0}
              >
                <Typography
                  color={theme.sk.color.text.primary}
                  flexShrink={1}
                  fontWeight="$medium"
                  numberOfLines={1}
                  size="$sm"
                >
                  {displayName}
                </Typography>
                <Badge color="muted">
                  {i18n.t(
                    'sales_portal.order_guides_list.add_items.pack_count_plural',
                    { count: packs.length },
                  )}
                </Badge>
              </XStack>
              <Typography
                color={theme.sk.color.text.secondary}
                numberOfLines={1}
                size="$xs"
                textAlign="left"
              >
                {parentSubtitle}
              </Typography>
            </YStack>
            {partlyAdded ? (
              <Typography
                color={theme.sk.color.text.tertiary}
                flexShrink={0}
                size="$xs"
              >
                {i18n.t(
                  'sales_portal.order_guides_list.add_items.pack_count_added',
                  { added: addedCount },
                )}
              </Typography>
            ) : null}
            <Animated.View style={chevronStyle}>
              <Icons.ChevronDown
                color={theme.sk.color.icon.tertiary}
                size={CHEVRON_DOWN_ICON_SIZE}
              />
            </Animated.View>
          </Pressable>
        </XStack>
      </XStack>
      {expanded
        ? packs.map((pack) => {
            const added = addedPackUUIDs.has(pack.variantPackUUID);
            const selected = selectedPackUUIDs.has(pack.variantPackUUID);
            const checked = added || selected;
            return (
              <Pressable
                accessibilityLabel={`${displayName} ${pack.packDisplayString}`}
                accessibilityRole="checkbox"
                accessibilityState={{ checked, disabled: added }}
                disabled={added}
                key={pack.variantPackUUID}
                onPress={() => onTogglePack(pack)}
                style={({ hovered }) => [
                  styles.subRow,
                  (selected || (hovered && !added)) && styles.selectedRow,
                  added && styles.dimmed,
                ]}
              >
                <XStack
                  flex={1}
                  gap={theme.sk.spacing.spaceSMedium}
                  minWidth={0}
                >
                  <Checkbox checked={checked} pointerEvents="none" size="$sm" />
                  <YStack flexShrink={1} gap="$2" minWidth={0}>
                    <Typography
                      color={theme.sk.color.text.primary}
                      numberOfLines={1}
                      size="$sm"
                    >
                      {pack.packDisplayString}
                    </Typography>
                    {pack.externalID ? (
                      <Typography
                        color={theme.sk.color.text.secondary}
                        numberOfLines={1}
                        size="$xs"
                      >
                        {pack.externalID}
                      </Typography>
                    ) : null}
                  </YStack>
                </XStack>
                {added ? addedLabel : null}
              </Pressable>
            );
          })
        : null}
    </YStack>
  );
};

const useStyles = createStyles((theme) => ({
  body: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    minWidth: 0,
  },
  dimmed: {
    opacity: ADDED_OPACITY,
  },
  identity: {
    flexShrink: 1,
    minWidth: 0,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowBorder: {
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
  },
  selectedRow: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  subRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    justifyContent: 'space-between',
    paddingBottom: theme.sk.spacing.spaceSmall,
    paddingLeft: SUB_ROW_PADDING,
    paddingRight: theme.sk.spacing.spaceSmall,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
}));
